<template>
  <component
    :is="tag"
    :id="id"
    v-focus
    class="n-button"
    :class="{
      [$attrs.class]: true,
      'n-button--outline': outlined,
      'n-button--disabled': disabled,
      'n-button--shadow': shadow,
      'n-button--loading': loading,
      'n-button--centered': centered,
      'n-button--full-width': fullWidth,
      'n-button--full-width-mobile': fullWidthMobile,
      'n-button--size-small': size === 'small',
      'n-button--size-large': size === 'large',
      'n-button--size-xlarge': size === 'xlarge',
      'n-button--primary': color === 'primary',
      'n-button--light-primary': color === 'light-primary',
      'n-button--white': color === 'white',
      'n-button--gray': color === 'gray',
      'n-button--indigo-100': color === 'indigo-100',
      'n-button--custom-bg': Object.keys(containerCssVars).includes('--button-background-color'),
      'n-button--custom-cl': Object.keys(containerCssVars).includes('--button-text-color'),
      'n-button--custom-border': Object.keys(containerCssVars).includes('--button-border-color')
    }"
    :disabled="disabled"
    :to="link"
    :style="[$attrs.style, containerCssVars]"
    :type="type"
    @click="onClick"
    :data-testid="dataTestid"
  >
    <slot>
      <component v-if="icon" :is="icon" class="mr10" :fill="fill" />
      {{ text }}
    </slot>
  </component>
</template>
<script lang="ts" setup>
import { useNuxtApp } from '#imports';
import { sendButtonClickedEvent } from '@/resources/tracking';
import { ROOT_PATH } from '@/resources/helpers';

interface Props {
  disabled?: boolean;
  color?: string;
  colors?: {
    textColor?: string;
    backgroundColor?: string;
    borderColor?: string;
  };
  text?: string;
  outlined?: boolean;
  shadow?: boolean;
  centered?: boolean;
  fullWidth?: boolean;
  loading?: boolean;
  size?: string;
  onClickEvent?: Function;
  fullWidthMobile?: boolean;
  link?: string | object;
  id?: string | null;
  icon?: object | null;
  type?: string | null;
  trackingOptions?: object | null;
  dataTestid?: string | null;
  fill?: string;
}

const props = withDefaults(defineProps<Props>(), {
  disabled: false,
  color: 'primary',
  type: 'button',
  // colors: () => ({}),
  text: 'Button',
  outlined: false,
  shadow: false,
  centered: false,
  fullWidth: false,
  loading: false,
  size: 'normal',
  onClickEvent: () => {},
  fullWidthMobile: false,
  link: undefined,
  id: null,
  icon: null
  // colors: {
  //   textColor: '#fff'
  // }
});

defineOptions({
  inheritAttrs: false
});

const attrs = useAttrs();

const { $router } = useNuxtApp();
const containerCssVars = computed(() => {
  const cssVars = {};

  if (props.colors && props.colors?.textColor?.length) {
    cssVars['--button-text-color'] = props.colors.textColor;
  }

  if (props.colors && props.colors?.backgroundColor?.length) {
    cssVars['--button-background-color'] = props.colors.backgroundColor;
  }

  if (props.colors && props.colors?.borderColor?.length) {
    cssVars['--button-border-color'] = props.colors.borderColor;
  }

  return cssVars;
});

const tag = computed(() => {
  if (props.link === null) {
    return 'button';
  }

  switch (typeof props.link) {
    case 'object':
    case 'string':
      return resolveComponent('nuxt-link');
  }

  return 'button';
});

async function onClick() {
  if (typeof props.onClickEvent === 'function') props.onClickEvent();
  if (typeof attrs.onClick === 'function') attrs.onClick();

  const trackingOptions = props.trackingOptions;

  return sendButtonClickedEvent({
    pagePath: $router.currentRoute.value.path,
    pageUrl: $router.currentRoute.value.fullPath,
    targetUrl: window.location.href + ROOT_PATH + (props.link && props.link?.name ? props.link.name : ' '),
    targetPath: props.link?.name,
    label: trackingOptions?.label ? trackingOptions?.label : props.text,
    citySelected: trackingOptions?.citySlected,
    listId: trackingOptions?.listId,
    category: trackingOptions?.category,
    elementId: trackingOptions?.elementId || props.id,
    hostId: trackingOptions?.hostId,
    locationCity: trackingOptions?.locationCity,
    motivLevel: trackingOptions?.motivLevel,
    dateSelected: trackingOptions?.dateSelected,
    itemName: trackingOptions?.itemName,
    buttonAction: trackingOptions?.buttonAction,
    value: trackingOptions?.value || props.id,
    pageTitle: trackingOptions?.pageTitle || props.id || 'no pageTitle'
  });
}
</script>

<style lang="scss" scoped>
@use '@/assets/styles/css_BASE/helpers/mixins/font-size.scss' as *;
@use '@/assets/styles/css_BASE/helpers/mixins/_position.scss' as *;
@use '@/assets/styles/css_BASE/helpers/mixins/_box.scss' as *;
@use '@/assets/styles/css_BASE/helpers/mixins/_bp.scss' as *;

.n-button {
  justify-content: center;
  border-radius: 4px;
  padding: 0 15px;
  height: var(--control-height, 42px);
  font-family: var(--ff-regular);
  border-width: 1px;
  border-style: solid;
  cursor: pointer;
  @include font-size(16px);
  display: flex;
  font-weight: 500;
  letter-spacing: 0.03em;
  align-items: center;

  &--shadow {
    box-shadow: 0 0 6px 2px rgba(0, 0, 0, 0.3);
  }

  &--loading {
    opacity: 0.6;
    position: relative;
    color: transparent !important;
    pointer-events: none;
    &::after {
      @include absolute(50%, auto, auto, 50%);
      @include pseudo-box(15px, 15px);
      margin-left: -15.5px;
      margin-top: -15.5px;
      animation: rotate 0.8s infinite linear;
      border: 8px solid #fff;
      border-right-color: transparent;
      border-radius: 50%;
    }
  }

  &--size-small {
    padding: 0 10px;
    height: var(--button-small-height);
    line-height: var(--button-small-height);
    @include font-size(12px);
  }

  &--size-large {
    padding: 1em 1.5em;
    line-height: 24px;
  }

  &--size-xlarge {
    padding: 1em 1.5em;
    line-height: 24px;
    @include bp(sm) {
      padding: 1.8em 6em;
      line-height: 24px;
    }
    @include bp(lg) {
      padding: 1.8em 6em;
      line-height: 24px;
    }
  }

  &--white {
    background: #fff;
    border-color: #fff;
    color: var(--color-primary);
  }

  &--primary {
    background: var(--color-primary);
    border-color: var(--color-primary);
  }

  &--light-primary {
    background: var(--color-light-primary);
    border-color: var(--color-primary);
    color: var(--color-primary);
  }

  &--indigo-100 {
    background: var(--color-indigo-100);
    border-color: var(--color-primary);
    color: var(--color-primary);

    &.n-button--loading {
      &::after {
        border-color: var(--color-primary);
        border-right-color: transparent;
      }
    }
  }

  &--outline {
    background: transparent;
    color: var(--color-primary);
  }

  &--centered {
    justify-content: center;
  }

  &--full-width {
    width: 100%;
  }
  &--full-width-mobile {
    width: 100%;
    @include bp(sm-up) {
      width: auto;
    }
  }

  &--gray {
    border: 2px solid var(--color-night-blue-900);
    color: var(--color-night-blue-900);
    background-color: white;
  }

  &--disabled {
    background: rgba(10, 7, 45, 0.16);
    color: #525666;
    border-color: #525666;
    cursor: not-allowed;
  }

  &--custom-bg {
    background: var(--button-background-color);
  }

  &--custom-cl {
    color: var(--button-text-color);
  }

  &--custom-border {
    border: var(--button-border-color) solid 1px;
  }

  &:not(.n-button--white):not(.n-button--disabled):not(.n-button--outline):not(.n-button--gray):not(
      .n-button--light-primary
    ):not(.n-button--custom-cl):not(.n-button--indigo-100) {
    color: #fff;
  }
}

a.n-button {
  display: inline-flex;
}
</style>
